import { Button } from 'antd'
import { createElement, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

import { STEPPER_CONFIG } from '../config/stepper-config'
import { StepperKey } from './constants/auth.constants'
import { StepperConfigItem } from './types/stepper-config'
import { RouteEnum } from '../../../constants/routes.enum'
import { useRegisterStore } from '../../../entities/auth/stores/register.store'
import { useCreateOrganization } from '../../../entities/organizations/hooks/organization.hooks'
import { useCreateProject } from '../../../entities/projects/hooks/project.hooks'
import { SubscriptionCycle, SubscriptionPlan, SubscriptionStatus } from '../../../entities/subscription/constants/subscription.contants'
import { useSubmitQuiz } from '../../../entities/user/hooks/user.hooks'
import { useUserStore } from '../../../entities/user/stores/user.store'
import { LocalStorageService } from '../../../utils/local-storage-service/local-storage-service'

const CURRENT_STEP_KEY = 'currentStep'
const USER_DATA_KEY = 'userData'

const getInitialStep = () => {
  return LocalStorageService.get(CURRENT_STEP_KEY) ?? StepperKey.CompanyConfig
}

const getInitialData = () => {
  const data = LocalStorageService.get(USER_DATA_KEY)

  return data ?? {}
}

export const OnboardingStepperPage = () => {
  const { t } = useTranslation()
  const { setStep } = useRegisterStore()
  const childRef = useRef<any | null>(null)
  const navigate = useNavigate()
  const [currentStepKey, setCurrentStepKey] = useState<string>(getInitialStep())
  const [userData, setUserData] = useState<any>(getInitialData())
  const currentStep = STEPPER_CONFIG.get(currentStepKey) as StepperConfigItem
  const { mutateAsync: createOrganization } = useCreateOrganization()
  const { mutateAsync: createProject } = useCreateProject()
  const { mutateAsync: submitQuiz } = useSubmitQuiz()
  const isPaid = useUserStore((state) => state.user?.isPaid)
  const updateUser = useUserStore((state) => state.updateUser)

  if (!currentStep) {
    return <div className="d-flex justify-content-center align-items-center">Loading...</div>
  }

  useEffect(() => {
    LocalStorageService.set(CURRENT_STEP_KEY, currentStepKey)
    setStep(currentStep.step)
  }, [currentStepKey])

  const handleNext = async () => {
    const isPaymentStep = currentStepKey === StepperKey.Payment
    if (isPaymentStep && !isPaid) {
      updateUser({
        isPaid: true,
      })
    }
    const errors = await childRef.current?.getValues().errors
    if (Object.keys(errors).length) {
      return
    }

    const stepUserData = childRef.current?.getValues().values

    const data = {
      ...userData,
      [currentStepKey]: stepUserData,
    }

    LocalStorageService.set('userData', data)
    setUserData(data)

    const nextStepKey = currentStep.next
    if (nextStepKey) {
      setCurrentStepKey(nextStepKey)
    }

    if (!nextStepKey) {
      const registerInfo = LocalStorageService.get(USER_DATA_KEY)
      try {
        const organization = await createOrganization({
          name: registerInfo[StepperKey.CompanyConfig].name,
          billing_cycle: SubscriptionCycle.Monthly,
          plan: SubscriptionPlan.Paid,
          enable_department: registerInfo[StepperKey.CompanyConfig].departments,
          enable_project: registerInfo[StepperKey.CompanyConfig].projects,
          account_balance: '0.00',
          subscription_status: SubscriptionStatus.Active,
        })
        console.log(organization)
        const project = await createProject({
          name: registerInfo[StepperKey.ProjectCreation].name,
          organizationId: organization.id,
        })
        if (project && organization) {
          await updateUser({
            projectId: project.id,
            organizationId: organization.id,
            isQuizPassed: true,
          })

          await submitQuiz()

          toast.success(t('onboarding.success'))
          navigate('/workspace')
        } else {
          toast.error(t('onboarding.error'))
        }
      } catch (error: any) {
        toast.error(error.message)
      }
    }
  }

  const handleBack = () => {
    const prevStepKey = currentStep.prev
    if (prevStepKey) {
      setCurrentStepKey(prevStepKey)
    } else {
      navigate('/' + RouteEnum.AUTH)
    }
  }

  return (
    <div className="d-flex flex-column justify-content-between h-75">
      {createElement(currentStep.component, {
        ref: childRef,
        initialValue: userData?.[currentStepKey] || {},
      })}

      <div className="d-flex justify-content-between align-items-center">
        <Button
          onClick={handleBack}
          size="small"
        >
          {t('onboarding.buttons.back')}
        </Button>

        <div className="d-flex col-4 col-md-3">
          <Button
            onClick={handleNext}
            size="small"
            type="primary"
            block
          >
            {t(currentStep.buttonText)}
          </Button>
        </div>
      </div>
    </div>
  )
}
